// components/PageSEO.js - Reusable SEO component for all pages
import { useEffect } from 'react';

const PageSEO = ({ 
  title, 
  description, 
  keywords = [], 
  canonicalUrl,
  ogImage,
  ogType = 'website',
  schema,
  noIndex = false,
  customMeta = {}
}) => {
  
  useEffect(() => {
    const baseUrl = 'https://kindersmeats.com';
    const defaultImage = 'https://kindersmeats.com/images/kinders-og-image.jpg';
    const businessName = "Kinder's Meats Deli BBQ & Catering";
    
    // Set document title
    document.title = title;

    // Helper function to update or create meta tags
    const updateMetaTag = (name, content, property = false) => {
      if (!content) return; // Skip if no content
      
      const attribute = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    };

    // Helper function to update or create link tags
    const updateLinkTag = (rel, href) => {
      if (!href) return;
      
      let link = document.querySelector(`link[rel="${rel}"]`);
      
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      
      link.setAttribute('href', href);
    };

    // Basic Meta Tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords.join(', '));
    updateMetaTag('robots', noIndex ? 'noindex, nofollow' : 'index, follow');
    updateMetaTag('author', businessName);
    updateMetaTag('viewport', 'width=device-width, initial-scale=1.0');
    
    // Canonical URL
    if (canonicalUrl) {
      updateLinkTag('canonical', canonicalUrl.startsWith('http') ? canonicalUrl : `${baseUrl}${canonicalUrl}`);
    }

    // Open Graph Meta Tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', ogImage || defaultImage, true);
    updateMetaTag('og:url', canonicalUrl ? (canonicalUrl.startsWith('http') ? canonicalUrl : `${baseUrl}${canonicalUrl}`) : window.location.href, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:site_name', businessName, true);
    updateMetaTag('og:locale', 'en_US', true);

    // Twitter Card Meta Tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', ogImage || defaultImage);
    updateMetaTag('twitter:site', '@KindersMeats'); // Update with actual Twitter handle

    // Business/Location Meta Tags
    updateMetaTag('geo.region', 'CA');
    updateMetaTag('geo.country', 'US');
    updateMetaTag('geo.placename', 'California');

    // Custom meta tags
    Object.entries(customMeta).forEach(([name, content]) => {
      updateMetaTag(name, content);
    });

    // Schema markup
    if (schema) {
      // Remove existing schema with the same ID
      const existingSchema = document.getElementById('page-schema');
      if (existingSchema) {
        existingSchema.remove();
      }

      // Add new schema
      const script = document.createElement('script');
      script.id = 'page-schema';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema, null, 2);
      document.head.appendChild(script);
    }

    // Cleanup function
    return () => {
      // Reset title when component unmounts (optional)
      document.title = businessName;
      
      // Remove schema when component unmounts
      const schemaScript = document.getElementById('page-schema');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, schema, noIndex, customMeta]);

  // This component doesn't render anything visible
  return null;
};

export default PageSEO;